/**
 * Folder Card Component
 * Displays a folder in a grid view
 */

import {
  Card,
  CardContent,
  CardActions,
  Typography,
  IconButton,
  Box,
  Tooltip,
  Skeleton,
} from '@mui/material'
import {
  Folder,
  FolderOpen,
  MoreVert,
} from '@mui/icons-material'
import type { MediaFolderDto } from '@api/types/files'

interface FolderCardProps {
  folder: MediaFolderDto
  onClick?: () => void
  onMenuClick?: (event: React.MouseEvent<HTMLElement>) => void
  isLoading?: boolean
}

export function FolderCard({
  folder,
  onClick,
  onMenuClick,
  isLoading,
}: FolderCardProps) {
  if (isLoading) {
    return (
      <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Skeleton variant="rectangular" height={120} />
        <CardContent sx={{ flexGrow: 1 }}>
          <Skeleton variant="text" width="80%" />
          <Skeleton variant="text" width="40%" />
        </CardContent>
      </Card>
    )
  }

  const folderColor = folder.color || '#FFC107' // Default amber color for folders

  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.2s',
        '&:hover': onClick
          ? {
              borderColor: 'primary.main',
              boxShadow: 2,
              '& .folder-icon': {
                transform: 'scale(1.1)',
              },
            }
          : {},
      }}
      onClick={onClick}
    >
      {/* Folder icon area */}
      <Box
        sx={{
          height: 120,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.50',
          position: 'relative',
        }}
      >
        <Folder
          className="folder-icon"
          sx={{
            fontSize: 64,
            color: folderColor,
            transition: 'transform 0.2s',
          }}
        />

        {/* Subfolder count badge */}
        {folder.subfolderCount > 0 && (
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              borderRadius: 1,
              px: 1,
              py: 0.25,
              fontSize: '0.75rem',
            }}
          >
            {folder.subfolderCount} folder{folder.subfolderCount !== 1 ? 's' : ''}
          </Box>
        )}
      </Box>

      {/* Folder info */}
      <CardContent sx={{ flexGrow: 1, pb: 1 }}>
        <Typography
          variant="subtitle2"
          noWrap
          title={folder.name || 'Unnamed Folder'}
          sx={{ fontWeight: 600 }}
        >
          {folder.name || 'Unnamed Folder'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {folder.fileCount} file{folder.fileCount !== 1 ? 's' : ''}
        </Typography>
        {folder.description && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: 'block',
              mt: 0.5,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={folder.description}
          >
            {folder.description}
          </Typography>
        )}
      </CardContent>

      {/* Actions */}
      <CardActions sx={{ pt: 0, justifyContent: 'space-between' }}>
        <Typography variant="caption" color="text.secondary">
          {new Date(folder.updatedDate).toLocaleDateString()}
        </Typography>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation()
            onMenuClick?.(e)
          }}
        >
          <MoreVert fontSize="small" />
        </IconButton>
      </CardActions>
    </Card>
  )
}

export function FolderCardSkeleton() {
  return (
    <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Skeleton variant="rectangular" height={120} />
      <CardContent sx={{ flexGrow: 1 }}>
        <Skeleton variant="text" width="80%" />
        <Skeleton variant="text" width="40%" />
      </CardContent>
    </Card>
  )
}
